import React, { useState } from 'react'
import {
    Box,
    Button,
    Card,
    CardContent,
    Collapse,
    Divider,
    IconButton,
    Typography,
    useTheme,
} from '@mui/material'
import {
    KeyboardArrowDown,
    KeyboardArrowUp,
    OpenInNew,
    ReceiptLong,
} from '@mui/icons-material'

import { getCurrency, getShortFormDate } from '../../helpers/stringHelpers'
import { TenantInvoice } from '../../types/invoice'
import {
    formatInvoiceLocation,
    getInvoiceServiceName,
    getLineItemLabel,
} from './invoiceUtils'

interface InvoiceCardProps {
    invoice: TenantInvoice
}

export const InvoiceCard: React.FC<InvoiceCardProps> = ({ invoice }) => {
    const theme = useTheme()
    const [expanded, setExpanded] = useState(false)
    const serviceName = getInvoiceServiceName(invoice)
    const items = invoice.items ?? []
    const hasMultipleItems = items.length > 1

    const handleViewPdf = (event: React.MouseEvent) => {
        event.stopPropagation()
        if (invoice.invoice) {
            window.open(invoice.invoice, '_blank', 'noopener,noreferrer')
        }
    }

    return (
        <Card
            sx={{
                bgcolor: 'rgba(255,255,255,0.04)',
                borderRadius: 2,
                boxShadow: 'none',
                border: '1px solid rgba(255,255,255,0.1)',
            }}
        >
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: expanded ? 1.5 : 1.5 } }}>
                <Box
                    onClick={() => setExpanded((prev) => !prev)}
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                        cursor: 'pointer',
                    }}
                >
                    <ReceiptLong
                        sx={{
                            fontSize: 22,
                            color: theme.palette.primary.main,
                            mt: 0.25,
                            flexShrink: 0,
                        }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'space-between',
                                gap: 1,
                                flexWrap: 'wrap',
                            }}
                        >
                            <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 600, color: 'rgba(255,255,255,0.9)' }}
                            >
                                {serviceName}
                            </Typography>
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    fontWeight: 600,
                                    color: theme.palette.primary.main,
                                    flexShrink: 0,
                                }}
                            >
                                {getCurrency(invoice.total_cost)}
                            </Typography>
                        </Box>
                        <Typography
                            variant="body2"
                            sx={{ color: 'rgba(255,255,255,0.5)', mt: 0.5 }}
                        >
                            {getShortFormDate(invoice.created_date)}
                            {hasMultipleItems && ` · ${getLineItemLabel(items.length)}`}
                        </Typography>
                    </Box>
                    <IconButton
                        size="small"
                        onClick={(event) => {
                            event.stopPropagation()
                            setExpanded((prev) => !prev)
                        }}
                        sx={{ color: 'rgba(255,255,255,0.6)', flexShrink: 0 }}
                        aria-label={expanded ? 'Collapse invoice details' : 'Expand invoice details'}
                    >
                        {expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    </IconButton>
                </Box>

                {invoice.invoice && (
                    <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<OpenInNew />}
                            onClick={handleViewPdf}
                            sx={{
                                textTransform: 'none',
                                borderColor: 'rgba(255,255,255,0.2)',
                                color: '#fff',
                            }}
                        >
                            View PDF
                        </Button>
                    </Box>
                )}

                <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 1.5 }} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                        {items.map((item) => (
                            <Box
                                key={item.id}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    gap: 2,
                                    alignItems: 'flex-start',
                                }}
                            >
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography
                                        variant="body2"
                                        sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}
                                    >
                                        {item.service}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{ color: 'rgba(255,255,255,0.5)' }}
                                    >
                                        {formatInvoiceLocation(item.location)}
                                    </Typography>
                                </Box>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: 'rgba(255,255,255,0.85)',
                                        fontWeight: 500,
                                        flexShrink: 0,
                                    }}
                                >
                                    {getCurrency(item.total_price)}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 1.5 }} />
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 600 }}
                        >
                            Total
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}
                        >
                            {getCurrency(invoice.total_cost)}
                        </Typography>
                    </Box>
                </Collapse>
            </CardContent>
        </Card>
    )
}
